import { Injectable } from '@angular/core';
import _ from 'lodash';
import { DataCollectorService } from '../data_collector_services/data_collector_service.service';
import { UtilityService } from '../utility_functions/utility_functions.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ProductType } from 'app/core/constants/constants';
import { bundlePricingLevel } from 'app/modules/products/products.types';

@Injectable({
    providedIn: 'root'
})

export class AccountingService {

    get accountingUrl(): any {
        return {
            tax: this.dataCollectorService.baseurl() + '/invoice_estimate/tax',
            taxCode: this.dataCollectorService.baseurl() + '/taxes/codes'
        };
    } 

    get financePromoUrl(): any {
        return this.dataCollectorService.baseurl()+ '/financing/payments/promo';
    };
    isTaxable: Observable<boolean>;
    masterTaxes: Observable<any>;
    applicableTaxes: Observable<any>;
    private isTaxableSubject$: BehaviorSubject<boolean> = new BehaviorSubject(null);
    private masterTaxesSubject$: BehaviorSubject<any> = new BehaviorSubject(null);
    private applicableTaxesSubject$: BehaviorSubject<any> = new BehaviorSubject(null);

    constructor(
        private utilityService: UtilityService,
        private dataCollectorService: DataCollectorService
    ) {
        this.isTaxable = this.isTaxableSubject$;
        this.masterTaxes = this.masterTaxesSubject$;
        this.applicableTaxes = this.applicableTaxesSubject$;
    }
    // tax Calculation Functions
    calculateSubTotal(approvedlineItems,considerBillable=false): any {
        let subTotal = 0;
        let billableTotal = 0;
        let nonBillableTotal = 0;
        _.each(approvedlineItems, (item: any) => {
            if (item?.line_item_type !== 'HEADER') {
                if(considerBillable === true && item?.is_billable === false){
                    nonBillableTotal += item.total;
                }else{
                    billableTotal += item?.total;
                }
            }
        });
        subTotal = considerBillable === true ? billableTotal : nonBillableTotal + billableTotal
        return subTotal;
    }

    calculateNonBillableTotal(approvedlineItems): any {
        let nonBillableTotal = 0;
        _.each(approvedlineItems, (item: any) => {
            if (item?.line_item_type !== 'HEADER' && item?.is_billable === false) {
                nonBillableTotal += item.total;
            }
        });
        return nonBillableTotal;
    }

    getTaxPercent(approvedLineItemsWithoutTax, tax): any {
        const subTotal = this.calculateSubTotal(approvedLineItemsWithoutTax);
        return (subTotal * (tax.tax_percent / 100));
    }

    calculateTotal(approvedlineItems, approvedLineItemsWithoutTax, allTaxes): any {
        const subTotal = Number(this.calculateSubTotal(approvedlineItems).toFixed(this.utilityService.getCurrencyDecimal()));
        let total = 0;
        _.each(allTaxes, (tax) => {
            total = total + Number(this.getTaxPercent(approvedLineItemsWithoutTax, tax).toFixed(this.utilityService.getCurrencyDecimal()));
        });
        return total + subTotal;
    }

    getTaxExemptState(): Observable<boolean> {
        return this.isTaxable;
    }

    setTaxExemptState(state: boolean): void {
        this.isTaxableSubject$.next(state);
    }

    getMasterTaxes(): Observable<any> {
        return this.masterTaxes;
    }

    setMasterTaxes(masterTaxes): void {
        this.masterTaxesSubject$.next(masterTaxes);
    }

    getApplicableTaxes(): Observable<any> {
        return this.applicableTaxes;
    }

    setApplicableTaxes(masterTaxes): void {
        this.applicableTaxesSubject$.next(masterTaxes);
    }

    hasAvalaraTax(details): boolean {
        const showAvalaraBanner = details?.taxation_meta && details.taxation_meta?.current_status && details.taxation_meta.current_status !== 'ERROR';
        const isAvalaraEnabled =  this.utilityService.companyConfig?.taxation?.is_enabled && showAvalaraBanner;
        return (showAvalaraBanner || isAvalaraEnabled);
    }

    getFinancingPromo(payload): Observable<any> {
        return this.dataCollectorService.postData(this.financePromoUrl, payload);
    }

    getLoanPromoDetails(promo): any{
        let loanPromoTagStart;
        let loanPromoAmount;
        let loanPromoTagline;

        const promoTagLineStart = '* Financing Option Available From' ;
        const indexofdollar = promo.indexOf('$');
        const indexofpermonth = promo.indexOf('/month');
        const monthCost = promo.slice(indexofdollar, indexofpermonth + 6);
        const tagLineContent = promo.slice(indexofpermonth + 6,promo.length);
        if(indexofdollar !== -1 && indexofpermonth !== -1){
          loanPromoTagStart = promoTagLineStart;
          loanPromoAmount = monthCost;
          loanPromoTagline = tagLineContent;
        }
        return {loanPromoTagStart, loanPromoAmount, loanPromoTagline};
    }

    getIndividualTax(total: number, rate: number): number{
        return total * (rate / 100);
    }

    getDeposit(total: number): number{
        let depositValue: number;
        const currencyDecimal = this.utilityService.getCurrencyDecimal();
        const depositConfig = this.utilityService?.companyConfig?.estimate?.deposit;
        if(depositConfig?.threshold_amount <= total){
          if(depositConfig?.type === 'PERCENTAGE'){
            depositValue = Number(this.getIndividualTax(total, depositConfig?.value).toFixed(currencyDecimal));
          }else if(depositConfig?.type === 'FIXED'){
            depositValue = (depositConfig?.value).toFixed(currencyDecimal);
          }
        }else{
            depositValue = 0;
        }
        if(total === 0){
            depositValue = 0;
        }
        return depositValue;
    }

    getUpdatedSellingPrice(markup: any,purchasePrice: any,sellingPrice: any): any{
        if(markup?.markup_value>=0 && markup?.markup_value !== null) {
          let updatedSellingPrice: number = 0;
          let fixedDecimalSellingPrice: any = 0;
          let updatedPurchasePrice = !this.utilityService.isEmptyOrUndefined(purchasePrice) ? purchasePrice : sellingPrice;
          switch (markup?.markup_type) {
            case 'FLAT':
              updatedSellingPrice = updatedPurchasePrice + markup?.markup_value;
              break;
            case 'PERCENTAGE':
              updatedSellingPrice = updatedPurchasePrice + (updatedPurchasePrice * (markup?.markup_value / 100));
              break;
            case 'MULTIPLIER':
              updatedSellingPrice = updatedPurchasePrice * markup?.markup_value;
              break;
            default:
              break;
          }
          if (updatedSellingPrice >= 0) {
            const currencyDecimal: number = this.utilityService.getCurrencyDecimal();
            fixedDecimalSellingPrice = updatedSellingPrice.toFixed(currencyDecimal);
          }
          return fixedDecimalSellingPrice >=0 ? fixedDecimalSellingPrice : updatedSellingPrice;
        }else{
          return sellingPrice;
        }
    }

      updatePricelistPrice(element: any,product: any,pricelistType: any,productsInPricelist: any,pricelistUid: any,pricelistDetails: any): any{
        if(!product){
          return;
        }
        
        const isPerItemPricelist: boolean = pricelistType === 'PER_ITEM';
        const isInPricelist: boolean = productsInPricelist?.includes(element?.product_uid);
        const shouldClearDiscount = pricelistUid === '' && !isPerItemPricelist && productsInPricelist?.length === 0;
        const shouldApplyPerItemDiscount = isPerItemPricelist && isInPricelist;
        const isBundleRollup = element?.product_type === ProductType.BUNDLE && element?.pricing_level === bundlePricingLevel.ROLLUP && element?.associated_products?.length;
    
        if(isBundleRollup){
          element?.associated_products?.forEach((associatedProduct) => {
            if (isPerItemPricelist) {
              const isAssociatedInPricelist: boolean = productsInPricelist?.includes(associatedProduct?.product?.product_uid);
              if(isAssociatedInPricelist){
                const associatedPricelistProduct: any = pricelistDetails?.line_items.find((lineItem) => lineItem?.product?.product_uid === associatedProduct?.product?.product_uid)?.product;
                associatedProduct = isAssociatedInPricelist ? this.updatePricelistPrice(associatedProduct,pricelistUid !== '' ? associatedPricelistProduct : associatedProduct?.product,pricelistType,productsInPricelist,pricelistUid,pricelistDetails) : associatedProduct;
              }
            } else {
              const associatedPricelistProduct: any = product?.associated_products?.find((lineItem) => lineItem?.product?.product_uid === associatedProduct?.product?.product_uid)?.product;
              associatedProduct = this.updatePricelistPrice(associatedProduct,associatedPricelistProduct,pricelistType,productsInPricelist,pricelistUid,pricelistDetails);
            }
          });
          element.price = element.unit_price = element?.associated_products?.reduce((acc, associatedProduct) => {
            return acc + ((associatedProduct?.price || associatedProduct?.unit_price) * associatedProduct?.quantity);
          }
          ,0);
        }else{
          if(pricelistType === 'PER_ITEM'){
            if(isInPricelist && pricelistUid !== '') {
              element.price = product?.price;
              element.unit_price = product?.price;
              element.markup = null;
              element.discount = 0;
              element.discount_type = 'FIXED';
            }else if(isInPricelist && this.utilityService.isEmptyOrUndefined(pricelistUid)){
              element.price = product?.price;
              element.unit_price = product?.price;
              element.markup = product?.markup;
              element.discount = product?.discount ?? 0;
              element.discount_type = product?.discount_type ?? 'FIXED';
            }else if(!(isInPricelist && pricelistUid !== '')){
              element.markup = product?.markup;
              if (shouldClearDiscount || shouldApplyPerItemDiscount) {
                element.discount = 0;
                element.discount_type = 'FIXED';
              }
            }
          }else{
            element.price = element.unit_price = product?.price;
            element.markup = product?.markup ?? null;
            element.markup_type = product?.markup_type ?? 'FLAT';
            element.discount = product?.discount ?? 0;
            element.discount_type = product?.discount_type ?? 'FIXED';
          }
        }
        return element;
      }

}
